import { t } from '@/utility/localization';
import LinkAwareText from '@/components/common/LinkAwareText';
import {
  FILE_ITEM_STATUS_ERROR,
  STATUS_VIDEO_PROCESSING
} from '@/contexts/LibrarySetupContext/constants';
import React from 'react';
import { STATUS_PUBLISHED, THUMBNAIL } from '../../constants';
import OptionMenuV2 from '../OptionMenuV2';
import { ReadMoreSingleLine } from '../ReadMoreSingleLine/ReadMoreSingleLine';
import HourGlassIcon01 from '@/components/common/IconsV2/HourGlassIcon01';
import classNames from 'classnames';
import FolderItemThumbnail from '../v3/FolderItem/FolderItemThumbnail';
import { useState } from 'react';
import Icon from '@/components/npl/Icon';

// type: view, editable
const className = 'c-AssetCard';

const AssetCard = (props) => {
  const {
    folderItem,
    isUploading,
    uploadedProgressBar,
    onFolderItemEditClick,
    draggableSnapshot,
    draggableProvided,
    isDraggingOver,
    onDeleteClick,
    metadataList,
    menuOptions,
    onThumbnailChange,
    minified,
    debug
  } = props;
  const [closeOptions, setCloseOptions] = useState(false);

  const {
    _id: id,
    type,
    title,
    description,
    status,
    thumbnail,
    processingPercentComplete
  } = folderItem;
  const roundedProcessingPercentComplete = Math.floor(
    processingPercentComplete
  );
  const isPublished = status === STATUS_PUBLISHED;
  const isProcessing = status === STATUS_VIDEO_PROCESSING;
  const isInErrorState = status === FILE_ITEM_STATUS_ERROR;

  const isDragging = draggableSnapshot?.isDragging;
  const thumbnailSrc = thumbnail ?? THUMBNAIL?.[type];
  const isDefaultThumbnail = thumbnailSrc === THUMBNAIL?.[type];

  const onEditItemClick = () => {
    onFolderItemEditClick({ item: folderItem });
  };

  const handleThumbnailChange = (newThumbnailImageSrc) => {
    onThumbnailChange(folderItem, newThumbnailImageSrc);
  };

  return (
    <div
      id={`c-AssetCard--${id}`}
      className={classNames(
        className,
        'group relative box-border w-full rounded-12 px-4',
        {
          'py-8': minified,
          'border-1 border-npl-neutral-light-solid-6 py-16': !minified,
          'bg-white-default hover:bg-npl-neutral-light-solid-2':
            !isInErrorState && !isDragging,
          'border-1 border-npl-error-light-3 bg-npl-error-light-2 hover:bg-npl-error-light-3':
            isInErrorState,
          'bg-npl-neutral-light-solid-2 shadow-npl-styles-shadow-01':
            isDragging,
          'pointer-events-none': isDraggingOver
        }
      )}
      onMouseEnter={() => setCloseOptions(false)}
      onMouseLeave={() => setCloseOptions(true)}>
      <div className="relative flex">
        {/* Drag Icon */}
        {/* Hide drag icon when is uploading, because BE won't return the item if its uploading */}
        {isUploading ? (
          <div className="mr-4 w-24"></div>
        ) : (
          <div
            {...draggableProvided?.dragHandleProps}
            className="mr-4 w-24 self-center rounded-8 hover:bg-neutral-90">
            <div className="hidden group-hover:block">
              <Icon name="dots-reorder-drag" fill="#666" />
            </div>
          </div>
        )}

        {/* Thumbnail */}
        <FolderItemThumbnail
          thumbnailSrc={thumbnailSrc}
          showRemoveOption={!isDefaultThumbnail}
          handleImgChange={handleThumbnailChange}
          showEditIconOnOverlay={true}
          showEditIconOnBottomRight={false}
        />

        <div className="flex-1">
          <div className="flex h-full flex-row">
            {/* Title, Desc & Metadata */}
            <div className="flex h-full w-full flex-col justify-center space-y-4 pr-16">
              <p className="max-w-[640px] text-label-lg font-medium text-npl-text-icon-on-light-surface-primary">
                {title}
                {debug ? ` | ${folderItem._id}` : ''}
              </p>
              {description || isInErrorState ? (
                <div className="w-full max-w-[640px]">
                  <ReadMoreSingleLine
                    maxHeight={'18px'}
                    className="text-label-md">
                    {isInErrorState ? (
                      <p className="text-label-sm text-npl-error-light-11">
                        {t('there-was-a-problem-uploading')}
                      </p>
                    ) : (
                      <div className="text-[12px] leading-[18px] text-npl-text-icon-on-light-surface-secondary ">
                        <LinkAwareText text={description} />
                      </div>
                    )}
                  </ReadMoreSingleLine>
                </div>
              ) : null}

              {isProcessing ? (
                <div className="flex animate-pulse items-center space-x-4 text-label-sm text-npl-sky-light-11">
                  <div className="flex items-center">
                    <HourGlassIcon01
                      fill="#007AA3"
                      width="10"
                      height="10"
                    />

                    <p className="ml-2">{t('processing1')}</p>
                  </div>
                  {roundedProcessingPercentComplete ? (
                    <>
                      <div>•</div>
                      <p>
                        {roundedProcessingPercentComplete}
                        {t('completed1')}
                      </p>
                    </>
                  ) : null}
                </div>
              ) : null}

              {isPublished && (
                <div className="flex w-auto max-w-[640px] flex-row items-center space-x-6 overflow-hidden overflow-ellipsis whitespace-nowrap">
                  {metadataList
                    ?.filter((item) => item !== '' && item)
                    .map((metadata, index, { length }) => {
                      return (
                        <div
                          key={index}
                          className="flex flex-row items-center gap-6">
                          {metadata !== '' && (
                            <div className="overflow-hidden overflow-ellipsis text-label-sm text-npl-text-icon-on-light-surface-secondary">
                              {metadata}
                            </div>
                          )}

                          {index !== length - 1 && (
                            <div
                              key={`${index}divider`}
                              className="h-4 w-4 rounded-full bg-npl-text-icon-on-light-surface-secondary"
                            />
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
            {/* Delete and Edit CTAs */}
            <div className="hidden items-center group-hover:flex">
              {isInErrorState ? (
                <button
                  className="flex h-32 w-32 items-center justify-center rounded-full mix-blend-multiply hover:bg-npl-neutral-light-solid-3"
                  onClick={onDeleteClick}>
                  <Icon name="trash-01" />
                </button>
              ) : (
                <>
                  {/* Edit Button */}
                  {isPublished && (
                    <button
                      className="flex h-32 w-32 items-center justify-center rounded-full mix-blend-multiply hover:bg-npl-neutral-light-solid-3"
                      onClick={onEditItemClick}>
                      <Icon name="pencil-01" width="18" height="18" />
                    </button>
                  )}
                  {/* Kebab Menu */}
                  <OptionMenuV2
                    menuIcon={
                      <Icon name="dots-vertical" width="18" height="18" />
                    }
                    customTriggerClassNames="rounded-full hover:bg-npl-neutral-light-solid-3 mix-blend-multiply"
                    options={[...menuOptions]}
                    close={closeOptions}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar to show uploading progress */}
      {isUploading && (
        <div
          style={{
            width: !isNaN(uploadedProgressBar)
              ? `${uploadedProgressBar}%`
              : '0%'
          }}
          className={`absolute bottom-0 left-0 h-[2px] rounded-b-full bg-black`}></div>
      )}
    </div>
  );
};
export default React.memo(AssetCard);
